// StudentList view用のvuex

import { getStudentList, getStatusList, getStudentCount } from "@/api/student";
import dayjs from "dayjs";
import { Module } from "vuex";
import { RootState, TOO_MANY_STUDENTS_THRESHOLD } from "..";
import {
  RoomSummaryWithRunningRegistrants,
  RunningRegistrant
} from "@/entities/room";
import { RunningTimer } from "@/entities/learning";

export type FetchState = "idle" | "processing" | "done" | "error";
export interface StudentListState {
  learningTerm: string;
  termStart: string;
  termStartTime: number;
  termEnd: string;
  termEndTime: number;
  baseDatas: { [key: string]: string | number | boolean }[];
  beforeLoad: boolean;
  runningRoomRegistrants: RunningRegistrant[];
  runningTimers: RunningTimer[];
  limit: number;
  lastVisibleRefPath: string | null;
  hasMore: boolean;
  studentCount: number | null;
  fetchState: FetchState;
  timerId: number;
  countProgress: number;
}

const studentListModule: Module<StudentListState, RootState> = {
  namespaced: true,

  state: {
    learningTerm: "過去7日間",
    termStart: "",
    termStartTime: 0,
    termEnd: "",
    termEndTime: 0,
    baseDatas: [],
    beforeLoad: true,
    runningRoomRegistrants: [], // 現在どこかの学習室に入室中の生徒の情報
    runningTimers: [], // 現在タイムキーパー利用中の生徒の情報
    limit: 1000,
    lastVisibleRefPath: null,
    hasMore: false,
    studentCount: null,
    fetchState: "idle",
    timerId: -1,
    countProgress: 0
  },

  getters: {},

  mutations: {
    SET_LEARNING_TERM(state, term: string) {
      state.learningTerm = term;
      if (term === "過去7日間") {
        const end = dayjs().locale("ja").add(-1, "day").endOf("day");
        const start = dayjs().locale("ja").add(-7, "day").startOf("day");
        state.termEnd = end.format("YYYY-MM-DD");
        state.termEndTime = end.unix();
        state.termStart = start.format("YYYY-MM-DD");
        state.termStartTime = start.unix();
      } else if (term === "過去4週間") {
        const end = dayjs().locale("ja").add(-1, "day").endOf("day");
        const start = dayjs().locale("ja").add(-28, "day").startOf("day");
        state.termEnd = end.format("YYYY-MM-DD");
        state.termEndTime = end.unix();
        state.termStart = start.format("YYYY-MM-DD");
        state.termStartTime = start.unix();
      }
    },
    SET_TERM_START(state, start: string) {
      state.termStart = start;
      state.termStartTime = dayjs(state.termStart)
        .locale("ja")
        .startOf("day")
        .unix();
      const startData = dayjs.unix(state.termStartTime);
      const endData = dayjs.unix(state.termEndTime);
      if (endData.diff(startData, "day") <= 0) {
        state.termEnd = start;
        state.termEndTime = dayjs(state.termEnd)
          .locale("ja")
          .endOf("day")
          .unix();
      }
    },
    SET_TERM_END(state, end: string) {
      state.termEnd = end;
      state.termEndTime = dayjs(state.termEnd).locale("ja").endOf("day").unix();
      const startData = dayjs.unix(state.termStartTime);
      const endData = dayjs.unix(state.termEndTime);

      if (endData.diff(startData, "day") <= 0) {
        state.termStart = end;
        state.termStartTime = dayjs(state.termStart)
          .locale("ja")
          .startOf("day")
          .unix();
      }
    },
    SET_STUDENT_LIST_DATAS(
      state,
      datas: { [key: string]: string | number | boolean }[] | null
    ) {
      //ロード中状態を維持しつつ、配列だけ初期化したい時はnullを渡す
      if (datas === null) {
        state.baseDatas = [];
        return;
      }

      if (datas.length === 0) {
        return;
      }
      state.baseDatas = datas;
      state.beforeLoad = false;
    },
    APPEND_STUDENT_LIST_DATAS(state, datas) {
      state.baseDatas = [...state.baseDatas, ...datas];
    },
    SET_RUNNING_ROOM_REGISTRANTS(state, payload) {
      state.runningRoomRegistrants = payload;
    },
    SET_RUNNING_TIMERS(state, payload) {
      state.runningTimers = payload;
    },
    SET_LIMIT(state, limit: number) {
      state.limit = limit;
    },
    SET_LAST_VISIBLE_REF_PATH(state, path: string | null) {
      state.lastVisibleRefPath = path;
    },
    SET_HAS_MORE(state, hasMore: boolean) {
      state.hasMore = hasMore;
    },
    SET_STUDENT_COUNT(state, count: number) {
      state.studentCount = count;
    },
    SET_FETCH_STATE(state, fetchState: FetchState) {
      state.fetchState = fetchState;
    },
    CLEAR(state) {
      state.learningTerm = "過去7日間";
      state.termStart = "";
      state.termStartTime = 0;
      state.termEnd = "";
      state.termEndTime = 0;
      state.baseDatas = [];
      state.beforeLoad = true;
      state.limit = 1000;
      state.lastVisibleRefPath = null;
      state.hasMore = false;
      state.studentCount = null;
    }
  },

  actions: {
    async getStudentListDatas(
      context,
      options?: { limit?: number; append?: boolean }
    ) {
      const limit = options?.limit || context.state.limit;
      const append = options?.append ?? false;
      const limitParam =
        limit > 0
          ? {
              limit: limit,
              lastVisiblePath: context.state.lastVisibleRefPath
            }
          : null;

      context.commit("SET_FETCH_STATE", "processing");

      try {
        const datas = await getStudentList(
          context.state.termStartTime,
          context.state.termEndTime,
          limitParam
        );
        // "suf-name"とはログインIDのことである
        if (append) {
          context.commit("APPEND_STUDENT_LIST_DATAS", datas);
        } else {
          context.commit("SET_STUDENT_LIST_DATAS", datas);
        }

        if (limitParam) {
          context.commit("SET_HAS_MORE", datas.length >= limit);
          if (datas.length > 0) {
            const lastItem = datas[datas.length - 1];
            const refPath = `schools/${lastItem.schoolDocId}/classrooms/${lastItem.classroomDocId}/students/${lastItem.id}`;
            context.commit("SET_LAST_VISIBLE_REF_PATH", refPath);
          }
        } else {
          context.commit("SET_HAS_MORE", false);
        }

        if (!context.state.hasMore) {
          context.commit("SET_FETCH_STATE", "done");
        }
      } catch (e) {
        context.commit("SET_FETCH_STATE", "error");
        throw e instanceof Error ? e : new Error("unexpected error");
      }
    },
    async fetchStudentsLazily(context, { override }: { override: boolean }) {
      if (override) {
        if (context.state.timerId > -1) {
          window.clearInterval(context.state.timerId);
        }
        context.commit("SET_FETCH_STATE", "idle");
        context.commit("SET_HAS_MORE", false);
        context.commit("SET_LAST_VISIBLE_REF_PATH", null);
        context.commit("SET_STUDENT_LIST_DATAS", null);
        context.state.timerId = -1;
        context.state.countProgress = 0;
      }

      const MAX_TRY = 1000; //念のための無限ループ予防
      let i = 0;
      try {
        while (
          context.state.fetchState !== "done" &&
          context.state.fetchState !== "error"
        ) {
          await context.dispatch("getStudentListDatas", {
            append: i === 0 ? false : true
          });

          if (context.state.timerId > -1) {
            window.clearInterval(context.state.timerId);
          }

          i++;
          context.state.countProgress = i * context.state.limit;

          context.state.timerId = window.setInterval(() => {
            context.state.countProgress++;
          }, 100);

          if (i > MAX_TRY) {
            context.commit("SET_FETCH_STATE", "error");
            throw new Error(
              "生徒の数が多すぎるか、取得時にエラーが発生しました。"
            );
          }
        }
      } finally {
        if (context.state.timerId > -1) {
          window.clearInterval(context.state.timerId);
          context.state.timerId = -1;
        }
      }
    },
    async getStatusList(context, { type, tutorDocId }) {
      const { rooms, timers } = await getStatusList({ type, tutorDocId });
      context.commit(
        "SET_RUNNING_ROOM_REGISTRANTS",
        rooms
          .map(
            (room: RoomSummaryWithRunningRegistrants) => room.runningRegistrants
          )
          .flat()
      );
      context.commit("SET_RUNNING_TIMERS", timers);
    },
    async getStudentCount(context) {
      const { studentCount } = await getStudentCount();
      context.commit("SET_STUDENT_COUNT", studentCount);
      if (studentCount > TOO_MANY_STUDENTS_THRESHOLD) {
        context.commit("SET_HAS_TOO_MANY_STUDENTS", true, { root: true });
      }
    }
  }
};

export default studentListModule;
